import { useEffect, useMemo, useState } from "react";
import { Button } from "@/shared/ui/button";
import { Calendar } from "@/shared/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/shared/ui/tabs";
import { CalendarIcon, Check, X } from "lucide-react";
import { endOfDay, format, startOfDay, type Locale } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { cn } from "@/shared/lib/utils";
import { useTranslation } from "@/shared/i18n";
import { isPresetActive, type DateRangePreset } from "./presets";
import { MonthGridPicker } from "./MonthGridPicker";
import { YearGridPicker } from "./YearGridPicker";
import { RangeInputPicker } from "./RangeInputPicker";

export type DateRangePickerMode = "presets" | "days" | "months" | "years" | "range";

interface DateRangePickerTabLabels {
  presets?: string;
  days?: string;
  months?: string;
  years?: string;
  range?: string;
}

interface DateRangePickerRangeLabels {
  startDate?: string;
  endDate?: string;
  cancel?: string;
  apply?: string;
  placeholder?: string;
}

interface DateRangePickerProps {
  dateRange: DateRange | undefined;
  setDateRange: (range: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  presets?: DateRangePreset[];
  tabLabels?: DateRangePickerTabLabels;
  rangeLabels?: DateRangePickerRangeLabels;
  defaultMode?: DateRangePickerMode | "auto";
  enabledModes?: DateRangePickerMode[];
}

const ALL_MODES: DateRangePickerMode[] = ["presets", "days", "months", "years", "range"];

function resolveLocale(lang: string): Locale {
  if (lang.startsWith("en")) return enUS;
  if (lang.startsWith("es")) return es;
  return ptBR;
}

function formatPresetRangeLabel(preset: DateRangePreset, locale: Locale): string {
  const r = preset.range();
  if (!r.from || !r.to) return "";
  const sameYear = r.from.getFullYear() === r.to.getFullYear();
  const currentYear = new Date().getFullYear();
  const includeYear = !sameYear || r.from.getFullYear() !== currentYear;
  const fmt = includeYear ? "d MMM yy" : "d MMM";
  const fromStr = format(r.from, fmt, { locale });
  const toStr = format(r.to, fmt, { locale });
  return `${fromStr} – ${toStr}`;
}

const CALENDAR_CLASSES = {
  months: "relative flex flex-col gap-2",
  month: "flex flex-col gap-2 capitalize",
  month_caption:
    "flex h-8 w-full items-center justify-center text-sm font-medium",
  caption_label: "text-sm font-medium text-white/90",
  nav: "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 z-10 px-1",
  button_previous:
    "h-9 w-9 inline-flex items-center justify-center rounded-md text-white/55 hover:bg-white/[0.06] hover:text-white p-0",
  button_next:
    "h-9 w-9 inline-flex items-center justify-center rounded-md text-white/55 hover:bg-white/[0.06] hover:text-white p-0",
  weekdays: "flex mb-1",
  weekday:
    "text-white/35 flex-1 select-none text-[0.7rem] font-normal capitalize",
  week: "flex w-full",
  day: "group/day relative h-10 w-10 select-none p-0 text-center text-sm text-white/85",
  range_start:
    "rounded-l-md bg-white/[0.06] [&>button]:!bg-primary [&>button]:!text-primary-foreground [&>button]:!rounded-md",
  range_end:
    "rounded-r-md bg-white/[0.06] [&>button]:!bg-primary [&>button]:!text-primary-foreground [&>button]:!rounded-md",
  range_middle:
    "bg-white/[0.06] [&>button]:!bg-transparent [&>button]:!text-white [&>button]:!rounded-none",
  today:
    "[&>button]:font-semibold [&>button]:text-white",
  outside: "text-white/25",
  disabled: "text-white/20 opacity-50",
} as const;

const TABS_CONTENT_CLASS =
  "m-0 px-4 pb-4 pt-3 outline-none ring-0 focus-visible:ring-0";

export function DateRangePicker({
  dateRange,
  setDateRange,
  placeholder,
  className,
  presets,
  tabLabels,
  rangeLabels,
  defaultMode = "auto",
  enabledModes,
}: DateRangePickerProps) {
  const { t, i18n } = useTranslation("date-range-picker");
  const locale = useMemo(() => resolveLocale(i18n.language || "pt-BR"), [i18n.language]);

  const resolvedPlaceholder = placeholder ?? t("placeholder");

  const resolvedTabLabels = useMemo(
    () => ({
      presets: tabLabels?.presets ?? t("tabs.presets"),
      days: tabLabels?.days ?? t("tabs.days"),
      months: tabLabels?.months ?? t("tabs.months"),
      years: tabLabels?.years ?? t("tabs.years"),
      range: tabLabels?.range ?? t("tabs.range"),
    }),
    [tabLabels, t]
  );

  const resolvedRangeLabels = useMemo(
    () => ({
      startDate: rangeLabels?.startDate ?? t("range.startDate"),
      endDate: rangeLabels?.endDate ?? t("range.endDate"),
      cancel: rangeLabels?.cancel ?? t("range.cancel"),
      apply: rangeLabels?.apply ?? t("range.apply"),
      placeholder: rangeLabels?.placeholder ?? t("range.placeholder"),
      invalidOrder: t("range.invalidOrder"),
    }),
    [rangeLabels, t]
  );

  const hasPresets = !!presets && presets.length > 0;

  const modes = useMemo(() => {
    const filtered = (enabledModes ?? ALL_MODES).filter((m) =>
      m === "presets" ? hasPresets : true
    );
    // Fallback de segurança: se o caller passar enabledModes inválido (e.g. apenas
    // ["presets"] sem presets), garantir ao menos "days" pra evitar Tabs.value
    // apontando pra um TabsContent inexistente.
    return filtered.length > 0 ? filtered : ["days" as DateRangePickerMode];
  }, [enabledModes, hasPresets]);

  const computeInitialMode = (): DateRangePickerMode => {
    if (defaultMode !== "auto" && modes.includes(defaultMode)) {
      return defaultMode;
    }
    if (hasPresets && presets!.some((p) => isPresetActive(p, dateRange))) {
      return "presets";
    }
    return modes[0]!;
  };

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<DateRangePickerMode>(computeInitialMode);
  const [month, setMonth] = useState<Date | undefined>(
    dateRange?.from ?? new Date()
  );

  // Sync month state APENAS com mudanças EXTERNAS em dateRange.
  // Não roda enquanto popover está aberto pra não interferir com a navegação
  // local do user no calendar (clicar 1º dia em mode="range" zera `to` e
  // dispararia este effect causando salto de mês indesejado).
  useEffect(() => {
    if (open) return;
    if (dateRange?.from) {
      setMonth((prev) =>
        !prev || prev.getTime() !== dateRange.from!.getTime()
          ? dateRange.from
          : prev
      );
    } else if (!dateRange?.from && !dateRange?.to) {
      setMonth(new Date());
    }
  }, [dateRange?.from, dateRange?.to, open]);

  // Re-detecta modo inicial quando popover abre (mudanças externas no dateRange)
  useEffect(() => {
    if (open) {
      setMode(computeInitialMode());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pré-formata labels dos presets (evita recalcular em cada render)
  const presetRangeLabels = useMemo(() => {
    const map = new Map<string, string>();
    if (presets) {
      for (const p of presets) {
        map.set(p.id, formatPresetRangeLabel(p, locale));
      }
    }
    return map;
  }, [presets, locale]);

  // Anúncio aria-live quando o range é definido
  const announcement = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return "";
    const fromStr = format(dateRange.from, "PPP", { locale });
    const toStr = format(dateRange.to, "PPP", { locale });
    return t("announcement.rangeSelected", { from: fromStr, to: toStr });
  }, [dateRange?.from, dateRange?.to, t, locale]);

  // Estado do draft do Range tab (lift state up — sobrevive a troca de aba)
  const [rangeDraft, setRangeDraft] = useState<DateRange | undefined>(
    dateRange
  );

  // Sync draft com dateRange externo APENAS quando popover está fechado.
  // Enquanto aberto, o draft é "owned" pelo user — não pode ser sobrescrito
  // por re-renders do pai (object identity) que mantenham as mesmas datas.
  useEffect(() => {
    if (!open) {
      setRangeDraft(dateRange);
    }
  }, [dateRange?.from, dateRange?.to, open]);

  const handleClearDates = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setDateRange(undefined);
    setMonth(new Date());
  };

  const handlePresetClick = (preset: DateRangePreset) => {
    const range = preset.range();
    setDateRange(range);
    if (range.from) setMonth(range.from);
    setOpen(false);
  };

  const handleRangeApply = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from) setMonth(range.from);
    setOpen(false);
  };

  const handleRangeCancel = () => {
    setRangeDraft(dateRange);
    setOpen(false);
  };

  const handleDaySelect = (date: Date | undefined) => {
    if (!date) {
      setDateRange(undefined);
      return;
    }

    const range = { from: startOfDay(date), to: endOfDay(date) };
    setDateRange(range);
    setMonth(date);
    setOpen(false);
  };

  const handlePeriodSelect = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from) setMonth(range.from);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal bg-[#1A1A1A] text-white/90 border-white/[0.06]",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {dateRange?.from ? (
            dateRange.to ? (
              <div className="flex items-center gap-1 min-w-0">
                <span className="truncate">
                  {format(dateRange.from, "dd/MM/yyyy", { locale })}
                </span>
                <span>–</span>
                <span className="truncate">
                  {format(dateRange.to, "dd/MM/yyyy", { locale })}
                </span>
                {/* Não usamos <button> aqui pra evitar nesting de <button>
                    dentro do <button> do PopoverTrigger (DOM inválido). */}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClearDates}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleClearDates(e);
                    }
                  }}
                  className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-white/[0.08] cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/60"
                  aria-label={t("clearAriaLabel")}
                >
                  <X className="h-3.5 w-3.5 text-red-500" />
                </span>
              </div>
            ) : (
              <span>
                {format(dateRange.from, "dd/MM/yyyy", { locale })} – ...
              </span>
            )
          ) : (
            <span className="text-white/60">{resolvedPlaceholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        aria-label={t("popoverAriaLabel")}
        className="w-[340px] max-w-[calc(100vw-1rem)] p-0 bg-[#161616] text-white/90 border-white/[0.08] shadow-2xl rounded-xl overflow-hidden"
        align="start"
        sideOffset={6}
        collisionPadding={8}
      >
        {/* Anúncio aria-live para screen readers */}
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {announcement}
        </span>

        <Tabs
          value={mode}
          onValueChange={(v) => setMode(v as DateRangePickerMode)}
        >
          <div className="px-3 pt-3 pb-1">
            <TabsList
              className={cn(
                "flex w-full h-auto bg-transparent p-0 rounded-none gap-1 justify-start",
                "flex-nowrap overflow-x-auto",
                "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              )}
            >
              {modes.map((m) => (
                <TabsTrigger
                  key={m}
                  value={m}
                  className={cn(
                    "shrink-0 text-[11px] font-medium px-2.5 py-1.5 rounded-full border transition-colors",
                    "border-white/[0.10] text-white/70 bg-transparent",
                    "hover:bg-white/[0.05] hover:text-white",
                    "data-[state=active]:bg-primary data-[state=active]:border-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                  )}
                >
                  {resolvedTabLabels[m]}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {hasPresets && (
            <TabsContent
              value="presets"
              className={TABS_CONTENT_CLASS}
            >
              <div className="flex max-h-[360px] flex-col overflow-y-auto">
                {presets!.map((preset) => {
                  const active = isPresetActive(preset, dateRange);
                  const rangeLabel = presetRangeLabels.get(preset.id) ?? "";
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => handlePresetClick(preset)}
                      aria-pressed={active}
                      className={cn(
                        "group flex items-center gap-3 rounded-md px-3 py-2 text-left transition-colors",
                        "hover:bg-white/[0.05]",
                        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/60",
                        active && "bg-white/[0.04]"
                      )}
                    >
                      <Check
                        aria-hidden="true"
                        className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          active ? "text-white opacity-100" : "opacity-0"
                        )}
                      />
                      <span
                        className={cn(
                          "flex-1 text-sm truncate",
                          active ? "text-white font-medium" : "text-white/85"
                        )}
                      >
                        {preset.label}
                      </span>
                      <span className="text-xs text-white/40 truncate">
                        {rangeLabel}
                      </span>
                    </button>
                  );
                })}
              </div>
            </TabsContent>
          )}

          <TabsContent
            value="days"
            className={cn(TABS_CONTENT_CLASS, "flex justify-center")}
          >
            <Calendar
              mode="single"
              selected={dateRange?.from}
              onSelect={handleDaySelect}
              month={month}
              onMonthChange={setMonth}
              numberOfMonths={1}
              locale={locale}
              showOutsideDays={false}
              classNames={CALENDAR_CLASSES}
              className="[--cell-size:2.5rem] [--rdp-day_button-border:0px] [--rdp-selected-border:0px] p-0"
            />
          </TabsContent>

          <TabsContent
            value="months"
            className={cn(TABS_CONTENT_CLASS, "flex justify-center")}
          >
            <MonthGridPicker
              selected={dateRange}
              onSelect={handlePeriodSelect}
              initialYear={month?.getFullYear()}
            />
          </TabsContent>

          <TabsContent
            value="years"
            className={cn(TABS_CONTENT_CLASS, "flex justify-center")}
          >
            <YearGridPicker selected={dateRange} onSelect={handlePeriodSelect} />
          </TabsContent>

          {/* O draft está liftado pro pai (rangeDraft), então NÃO precisamos de
              forceMount pra preservar estado entre abas — basta o RangeInputPicker
              ser totalmente controlado. Evita manter Calendar pesado em DOM
              enquanto outra aba está ativa. */}
          <TabsContent
            value="range"
            className={TABS_CONTENT_CLASS}
          >
            <RangeInputPicker
              draft={rangeDraft}
              onDraftChange={setRangeDraft}
              onApply={handleRangeApply}
              onCancel={handleRangeCancel}
              labels={resolvedRangeLabels}
              calendarClassNames={CALENDAR_CLASSES}
              initialMonth={month}
              locale={locale}
            />
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
